// A10: Team Recipes tab. Org-scoped portable persona + watcher
// bundles. From this tab the Captain can:
//
//   - Snapshot the current workspace's seats + watchers into a named
//     recipe (lives on the org).
//   - Browse existing recipes on the org's shelf.
//   - Fork a recipe into this workspace (clones all seats + watchers
//     with their model bindings + trigger configs).
//   - Drop a recipe from the shelf (recipe_use history stays so
//     aggregate scoring doesn't lose its anchor).
//
// v1 lives in workspace/tabs/ because the entry point is always from a
// workspace's chrome. If a second entry point appears (settings-level
// recipe library), extract to a top-level `recipes/` module per Reef
// Q4 direction.

import { useMemo, useState } from "react";
import { useTable } from "spacetimedb/react";
import { tables } from "../../../spacetimedb-bindings";
import {
  ApiError,
  createRecipe,
  deleteRecipe,
  forkRecipe,
} from "../../api/client";
import { TabScrollbox } from "../../chrome/TabScrollbox";

interface Props {
  workspaceId: number;
  orgId: number | null;
}

interface Recipe {
  recipe_id: number;
  org_id: number;
  name: string;
  notes: string;
  autonomy_level: string;
  origin_workspace_id: number;
  seats_count: number;
  watchers_count: number;
  created_at: number;
}

interface SeatEntry {
  display_name?: string;
  role_title?: string;
  model?: string;
}

function bigintToNumber(v: unknown): number {
  if (typeof v === "bigint") return Number(v);
  if (typeof v === "number") return v;
  return 0;
}

function countArray(json: string): number {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? parsed.length : 0;
  } catch {
    return 0;
  }
}

function parseSeats(json: string): SeatEntry[] {
  try {
    const parsed = JSON.parse(json);
    return Array.isArray(parsed) ? (parsed as SeatEntry[]) : [];
  } catch {
    return [];
  }
}

export function RecipesTab({ workspaceId, orgId }: Props) {
  const [recipeRows] = useTable(tables.team_recipe);
  const [useRows] = useTable(tables.recipe_use);

  const recipes = useMemo<Recipe[]>(() => {
    if (orgId == null) return [];
    return recipeRows
      .filter((r) => bigintToNumber(r.orgId) === orgId)
      .map((r) => ({
        recipe_id: bigintToNumber(r.recipeId),
        org_id: bigintToNumber(r.orgId),
        name: r.name,
        notes: r.notes ?? "",
        autonomy_level: r.autonomyLevel ?? "",
        origin_workspace_id: bigintToNumber(r.originWorkspaceId),
        seats_count: countArray(r.seatsJson ?? "[]"),
        watchers_count: countArray(r.watchersJson ?? "[]"),
        created_at: bigintToNumber(r.createdAt),
      }))
      .sort((a, b) => b.created_at - a.created_at);
  }, [recipeRows, orgId]);

  const forksByRecipe = useMemo(() => {
    const map = new Map<number, number>();
    for (const row of useRows) {
      const rid = bigintToNumber(row.recipeId);
      map.set(rid, (map.get(rid) ?? 0) + 1);
    }
    return map;
  }, [useRows]);

  if (orgId == null) {
    return (
      <TabScrollbox>
        <div className="px-6 py-8 text-sm text-muted">
          Recipes are org-scoped; this workspace isn't attached to an org
          yet.
        </div>
      </TabScrollbox>
    );
  }

  return (
    <TabScrollbox>
      <header className="mb-4">
        <p className="text-xs text-muted">
          Portable team recipes — snapshot the current workspace's working
          agents + watchers + autonomy level, fork into other workspaces.
          Aggregate scores accrue per recipe across every workspace it's
          used in.
        </p>
      </header>

      <CreateRecipeCard orgId={orgId} originWorkspaceId={workspaceId} />

      {recipes.length === 0 ? (
        <div className="mt-4 rounded-md border border-edge bg-surface-raised px-6 py-8 text-center">
          <p className="text-sm text-muted">
            No recipes on the shelf yet. Snapshot this workspace above to
            seed one.
          </p>
        </div>
      ) : (
        <section className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
          {recipes.map((r) => (
            <RecipeCard
              key={r.recipe_id}
              recipe={r}
              forks={forksByRecipe.get(r.recipe_id) ?? 0}
              targetWorkspaceId={workspaceId}
            />
          ))}
        </section>
      )}
    </TabScrollbox>
  );
}

function CreateRecipeCard({
  orgId,
  originWorkspaceId,
}: {
  orgId: number;
  originWorkspaceId: number;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await createRecipe(orgId, {
        name: name.trim(),
        notes: notes.trim(),
        origin_workspace_id: originWorkspaceId,
      });
      setName("");
      setNotes("");
      setOpen(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="rounded-md border border-accent bg-accent/10 px-3 py-1.5 text-xs font-medium text-accent hover:bg-accent/20"
        data-testid="recipe-new-open"
      >
        + Snapshot this workspace as a recipe
      </button>
    );
  }
  return (
    <div
      className="flex flex-col gap-2 rounded-md border border-edge bg-surface-raised p-3"
      data-testid="recipe-new-form"
    >
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Recipe name…"
        className="rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
        data-testid="recipe-new-name"
      />
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes (optional)"
        rows={2}
        className="resize-none rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
        data-testid="recipe-new-notes"
      />
      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-md border border-edge px-2 py-1 text-[0.625rem] text-muted hover:text-primary"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || !name.trim()}
          className="rounded-md border border-accent bg-accent/10 px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/20 disabled:opacity-50"
          data-testid="recipe-new-submit"
        >
          {busy ? "Saving…" : "Save recipe"}
        </button>
      </div>
      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </div>
  );
}

function RecipeCard({
  recipe,
  forks,
  targetWorkspaceId,
}: {
  recipe: Recipe;
  forks: number;
  targetWorkspaceId: number;
}) {
  const [busy, setBusy] = useState<"fork" | "delete" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [armedDelete, setArmedDelete] = useState(false);

  const fork = async () => {
    setBusy("fork");
    setError(null);
    try {
      await forkRecipe(recipe.recipe_id, targetWorkspaceId);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const drop = async () => {
    if (!armedDelete) {
      setArmedDelete(true);
      setTimeout(() => setArmedDelete(false), 3000);
      return;
    }
    setBusy("delete");
    setError(null);
    try {
      await deleteRecipe(recipe.recipe_id);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <article
      className="flex flex-col gap-2 rounded-md border border-edge bg-surface-raised p-3"
      data-testid={`recipe-card-${recipe.recipe_id}`}
    >
      <header>
        <h3 className="text-sm font-semibold text-primary">{recipe.name}</h3>
        <p className="mt-0.5 font-code text-[0.625rem] text-faint">
          {recipe.seats_count} seat
          {recipe.seats_count === 1 ? "" : "s"} · {recipe.watchers_count}{" "}
          watcher
          {recipe.watchers_count === 1 ? "" : "s"} ·{" "}
          {forks === 0 ? "unused" : `${forks} use${forks === 1 ? "" : "s"}`}
        </p>
      </header>
      {recipe.notes && (
        <p className="text-[0.625rem] text-muted">{recipe.notes}</p>
      )}
      <p className="text-[0.625rem] text-faint">
        Autonomy: {recipe.autonomy_level}
      </p>
      <div className="mt-auto flex items-center justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={() => void drop()}
          disabled={busy !== null}
          className={`rounded-md border px-2 py-1 text-[0.625rem] transition-colors disabled:opacity-50 ${
            armedDelete
              ? "border-danger text-danger animate-pulse"
              : "border-edge text-muted hover:border-danger hover:text-danger"
          }`}
          data-testid={`recipe-card-${recipe.recipe_id}-delete`}
        >
          {armedDelete ? "Confirm" : "Drop"}
        </button>
        <button
          type="button"
          onClick={() => void fork()}
          disabled={busy !== null}
          className="rounded-md border border-accent px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/10 disabled:opacity-50"
          data-testid={`recipe-card-${recipe.recipe_id}-fork`}
        >
          {busy === "fork" ? "Forking…" : "Fork into this workspace"}
        </button>
      </div>
      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </article>
  );
}

// Re-export parseSeats so the drawer can show a preview list. Kept here
// because seat shape is recipe-local — consumers shouldn't parse the
// raw JSON themselves.
export { parseSeats };
